import { find, findIndex, isNil } from 'lodash';
import {
  CUSTOMER_LIST_REQUEST,
  CUSTOMER_LIST_REQUEST_ERROR,
  CUSTOMER_LIST_REQUEST_SUCCESS,
  CUSTOMER_SEARCH,
  CUSTOMER_SEARCH_ERROR,
  CUSTOMER_SEARCH_SUCCESS,
  CUSTOMER_SEARCH_LIST_CLEAR,
  CUSTOMER_ADD,
  CUSTOMER_ADD_ERROR,
  CUSTOMER_ADD_SUCCESS,
  CUSTOMER_REQUEST,
  CUSTOMER_REQUEST_SUCCESS,
  CUSTOMER_REQUEST_ERROR,
  CUSTOMER_REMOVE,
  CUSTOMER_REMOVE_ERROR,
  CUSTOMER_REMOVE_SUCCESS,
  CUSTOMER_UPDATE,
  CUSTOMER_UPDATE_ERROR,
  CUSTOMER_UPDATE_SUCCESS,
  CUSTOMER_LIST_EXPORT,
  CUSTOMER_LIST_EXPORT_SUCCESS,
  CUSTOMER_LIST_EXPORT_ERROR,
  CUSTOMER_CHANGE_STATUS_REQUEST,
  CUSTOMER_CHANGE_STATUS_REQUEST_SUCCESS,
  CUSTOMER_CHANGE_STATUS_REQUEST_ERROR,
  CUSTOMER_DOCUMENT_DOWNLOAD,
  CUSTOMER_DOCUMENT_DOWNLOAD_SUCCESS,
  CUSTOMER_DOCUMENT_DOWNLOAD_ERROR,
  CUSTOMER_UPLOAD_IMAGE,
  CUSTOMER_UPLOAD_IMAGE_SUCCESS,
  CUSTOMER_UPLOAD_IMAGE_ERROR,
  CUSTOMER_INCREASE_LIMIT,
  CUSTOMER_INCREASE_LIMIT_SUCCESS,
  CUSTOMER_INCREASE_LIMIT_ERROR,
  CUSTOMER_DOWNLOAD_CREDIT_DATABASE,
  CUSTOMER_DOWNLOAD_CREDIT_DATABASE_SUCCESS,
  CUSTOMER_DOWNLOAD_CREDIT_DATABASE_ERROR,
  CUSTOMER_UPDATE_CONTRACT_DATE,
  CUSTOMER_UPDATE_CONTRACT_DATE_ERROR,
  CUSTOMER_UPDATE_CONTRACT_DATE_SUCCESS,
  CUSTOMER_DOWNLOAD_REQUEST,
  CUSTOMER_DOWNLOAD_REQUEST_SUCCESS,
  CUSTOMER_DOWNLOAD_REQUEST_ERROR,
} from '@/store/actions/customer';
import { handleRequestError } from '@/utils/error';

import Repository, { CUSTOMERS } from '@/repositories/RepositoryFactory';

const CustomersRepository = Repository.get(CUSTOMERS);

const state = {
  customer: '',
  customerList: [],
  customerListStatus: '',
  customerLastCreated: '',
  customerMeta: '',
  customerStatus: '',
  customerExportStatus: '',
  customerExportLink: '',
  customerSearchList: '',
  customerSearchStatus: '',
  customerDocumentDownloadStatus: '',
  customerDocumentDownloadLink: '',
  customerUploadImageStatus: '',
  customerIncreaseLimitStatus: '',
  customerDownloadCreditDatabaseStatus: '',
  customerUpdateContractDateStatus: '',
  customerDownloadStatus: '',
  customerDownloadResponse: '',
};

const getters = {
  getCustomerById: (state, getters) => id => {
    if (getters.customerList.length > 0) {
      return find(getters.customerList, { id });
    }

    return state.service;
  },
  customer: state => state.customer,
  customerList: state => state.customerList,
  customerListStatus: state => state.customerListStatus,
  customerLastCreated: state => state.customerLastCreated,
  customerMeta: state => state.customerMeta,
  customerStatus: state => state.customerStatus,
  customerExportStatus: state => state.customerExportStatus,
  customerExportLink: state => state.customerExportLink,
  customerSearchList: state => state.customerSearchList,
  customerSearchStatus: state => state.customerSearchStatus,
  customerDocumentDownloadStatus: state => state.customerDocumentDownloadStatus,
  customerDocumentDownloadLink: state => state.customerDocumentDownloadLink,
  customerUploadImageStatus: state => state.customerUploadImageStatus,
  customerIncreaseLimitStatus: state => state.customerIncreaseLimitStatus,
  customerDownloadCreditDatabaseStatus: state => state.customerDownloadCreditDatabaseStatus,
  customerUpdateContractDateStatus: state => state.customerUpdateContractDateStatus,
  customerDownloadStatus: state => state.customerDownloadStatus,
  customerDownloadResponse: state => state.customerDownloadResponse,
};

const actions = {
  [CUSTOMER_LIST_REQUEST]: async ({ commit }, payload) => {
    commit(CUSTOMER_LIST_REQUEST);
    try {
      const response = await CustomersRepository.get(payload);
      commit(CUSTOMER_LIST_REQUEST_SUCCESS, response.data);
    } catch (error) {
      commit(CUSTOMER_LIST_REQUEST_ERROR);
      handleRequestError(error);
    }
  },

  [CUSTOMER_SEARCH]: async ({ commit }, payload) => {
    commit(CUSTOMER_SEARCH);
    try {
      let items = [];
      if (payload.query) {
        const response = await CustomersRepository.get(payload.businessId, payload.data);
        items = response.data.data;
      }

      commit(CUSTOMER_SEARCH_SUCCESS, items);
    } catch (error) {
      commit(CUSTOMER_SEARCH_ERROR);
      handleRequestError(error);
    }
  },

  [CUSTOMER_SEARCH_LIST_CLEAR]: async ({ commit }) => {
    commit(CUSTOMER_SEARCH_LIST_CLEAR);
  },

  [CUSTOMER_REQUEST]: async ({ commit }, payload) => {
    commit(CUSTOMER_REQUEST);
    try {
      const response = await CustomersRepository.getSelf(payload);
      const { data } = response;
      commit(CUSTOMER_REQUEST_SUCCESS, data);
    } catch (error) {
      commit(CUSTOMER_REQUEST_ERROR);
      handleRequestError(error);
    }
  },

  [CUSTOMER_ADD]: async ({ commit }, payload) => {
    commit(CUSTOMER_ADD);
    try {
      const response = await CustomersRepository.create(payload.businessId, payload.data);
      commit(CUSTOMER_ADD_SUCCESS, response.data.data);
    } catch (error) {
      commit(CUSTOMER_ADD_ERROR);
      handleRequestError(error);
    }
  },

  [CUSTOMER_UPDATE]: async ({ commit }, payload) => {
    commit(CUSTOMER_UPDATE);
    try {
      const response = await CustomersRepository.update(payload.id, payload.data);
      commit(CUSTOMER_UPDATE_SUCCESS, response.data.data);
    } catch (error) {
      commit(CUSTOMER_UPDATE_ERROR);
      handleRequestError(error);
    }
  },

  [CUSTOMER_REMOVE]: async ({ commit }, payload) => {
    commit(CUSTOMER_REMOVE);
    try {
      await CustomersRepository.delete(payload.businessId, payload.id);
      commit(CUSTOMER_REMOVE_SUCCESS, payload.id);
    } catch (error) {
      commit(CUSTOMER_REMOVE_ERROR);
      handleRequestError(error);
    }
  },

  [CUSTOMER_LIST_EXPORT]: async ({ commit }, payload) => {
    commit(CUSTOMER_LIST_EXPORT);
    try {
      const response = await CustomersRepository.export(payload.businessId, payload.data);
      commit(CUSTOMER_LIST_EXPORT_SUCCESS, response.data.data);
    } catch (error) {
      commit(CUSTOMER_LIST_EXPORT_ERROR);
      handleRequestError(error);
    }
  },

  [CUSTOMER_CHANGE_STATUS_REQUEST]: async ({ commit }, payload) => {
    commit(CUSTOMER_CHANGE_STATUS_REQUEST);
    try {
      const response = await CustomersRepository.changeStatus(payload.id, payload.action);
      commit(CUSTOMER_CHANGE_STATUS_REQUEST_SUCCESS, response.data.data);
    } catch (error) {
      commit(CUSTOMER_CHANGE_STATUS_REQUEST_ERROR);
      handleRequestError(error);
    }
  },

  [CUSTOMER_DOCUMENT_DOWNLOAD]: async ({ commit }, payload) => {
    commit(CUSTOMER_DOCUMENT_DOWNLOAD);
    try {
      const response = await CustomersRepository.downloadDocument(payload.id, payload.data);
      commit(CUSTOMER_DOCUMENT_DOWNLOAD_SUCCESS, response.data.data);
    } catch (error) {
      commit(CUSTOMER_DOCUMENT_DOWNLOAD_ERROR);
      handleRequestError(error);
    }
  },

  [CUSTOMER_UPLOAD_IMAGE]: async ({ commit }, payload) => {
    commit(CUSTOMER_UPLOAD_IMAGE);
    try {
      await CustomersRepository.uploadImage(payload.id, payload.data);
      commit(CUSTOMER_UPLOAD_IMAGE_SUCCESS);
    } catch (error) {
      commit(CUSTOMER_UPLOAD_IMAGE_ERROR);
      handleRequestError(error);
    }
  },

  [CUSTOMER_INCREASE_LIMIT]: async ({ commit }, payload) => {
    commit(CUSTOMER_INCREASE_LIMIT);
    try {
      const response = await CustomersRepository.increaseLimit(payload.id, payload.data);
      commit(CUSTOMER_INCREASE_LIMIT_SUCCESS, response.data.data);
    } catch (error) {
      commit(CUSTOMER_INCREASE_LIMIT_ERROR);
      handleRequestError(error);
    }
  },

  [CUSTOMER_DOWNLOAD_CREDIT_DATABASE]: async ({ commit }, payload) => {
    commit(CUSTOMER_DOWNLOAD_CREDIT_DATABASE);
    try {
      const response = await CustomersRepository.downloadCreditDatabase(payload.id);
      commit(CUSTOMER_DOWNLOAD_CREDIT_DATABASE_SUCCESS, response.data.data);
    } catch (error) {
      commit(CUSTOMER_DOWNLOAD_CREDIT_DATABASE_ERROR);
      handleRequestError(error);
    }
  },

  [CUSTOMER_UPDATE_CONTRACT_DATE]: async ({ commit }, payload) => {
    commit(CUSTOMER_UPDATE_CONTRACT_DATE);
    try {
      const response = await CustomersRepository.updateContractDate(payload.id, payload.data);
      commit(CUSTOMER_UPDATE_CONTRACT_DATE_SUCCESS, response.data.data);
    } catch (error) {
      commit(CUSTOMER_UPDATE_CONTRACT_DATE_ERROR);
      handleRequestError(error);
    }
  },

  [CUSTOMER_DOWNLOAD_REQUEST]: async ({ commit }, payload) => {
    commit(CUSTOMER_DOWNLOAD_REQUEST);
    try {
      const response = await CustomersRepository.download(payload);
      commit(CUSTOMER_DOWNLOAD_REQUEST_SUCCESS, response.data);
    } catch (error) {
      commit(CUSTOMER_DOWNLOAD_REQUEST_ERROR);
      handleRequestError(error);
    }
  },
};

const mutations = {
  [CUSTOMER_LIST_REQUEST]: state => {
    state.customerListStatus = 'loading';
  },
  [CUSTOMER_LIST_REQUEST_SUCCESS]: (state, payload) => {
    state.customerListStatus = 'success';
    state.customerList = payload.data;
    state.customerMeta = payload.meta;
  },
  [CUSTOMER_LIST_REQUEST_ERROR]: state => {
    state.customerListStatus = 'error';
  },

  [CUSTOMER_SEARCH]: state => {
    state.customerSearchStatus = 'loading';
  },
  [CUSTOMER_SEARCH_SUCCESS]: (state, payload) => {
    state.customerSearchStatus = 'success';
    state.customerSearchList = payload;
  },
  [CUSTOMER_SEARCH_ERROR]: state => {
    state.customerSearchStatus = 'error';
  },

  [CUSTOMER_SEARCH_LIST_CLEAR]: state => {
    state.customerSearchList = [];
  },

  [CUSTOMER_ADD]: state => {
    state.customerStatus = 'loading';
    state.customerLastCreated = '';
  },
  [CUSTOMER_ADD_SUCCESS]: (state, payload) => {
    state.customerStatus = 'success';
    state.customerLastCreated = payload;
    state.customerList.push(payload);
  },
  [CUSTOMER_ADD_ERROR]: state => {
    state.customerStatus = 'error';
  },

  [CUSTOMER_LIST_EXPORT]: state => {
    state.customerExportStatus = 'loading';
    state.customerExportLink = '';
  },
  [CUSTOMER_LIST_EXPORT_SUCCESS]: (state, payload) => {
    state.customerExportStatus = 'success';
    state.customerExportLink = payload;
  },
  [CUSTOMER_LIST_EXPORT_ERROR]: state => {
    state.customerExportStatus = 'error';
  },

  [CUSTOMER_REQUEST]: state => {
    state.customerStatus = 'loading';
  },
  [CUSTOMER_REQUEST_SUCCESS]: (state, payload) => {
    state.customerStatus = 'success';
    state.customer = payload.data;
  },
  [CUSTOMER_REQUEST_ERROR]: state => {
    state.customerStatus = 'error';
  },

  [CUSTOMER_UPDATE]: state => {
    state.customerStatus = 'loading';
  },
  [CUSTOMER_UPDATE_SUCCESS]: (state, payload) => {
    state.customerStatus = 'success';
    const index = findIndex(state.customerList, { id: payload.id });
    state.customerList[index] = payload;
  },
  [CUSTOMER_UPDATE_ERROR]: state => {
    state.customerStatus = 'error';
  },

  [CUSTOMER_REMOVE]: state => {
    state.customerStatus = 'deleting';
  },
  [CUSTOMER_REMOVE_SUCCESS]: (state, payload) => {
    const index = findIndex(state.customerList, { id: payload });
    state.customerList.splice(index, 1);
  },
  [CUSTOMER_REMOVE_ERROR]: state => {
    state.customerStatus = 'error';
  },

  [CUSTOMER_CHANGE_STATUS_REQUEST]: state => {
    state.customerStatus = 'changing';
  },
  [CUSTOMER_CHANGE_STATUS_REQUEST_SUCCESS]: state => {
    state.customerStatus = 'success';
  },
  [CUSTOMER_CHANGE_STATUS_REQUEST_ERROR]: state => {
    state.customerStatus = 'error';
  },

  [CUSTOMER_DOCUMENT_DOWNLOAD]: state => {
    state.customerDocumentDownloadStatus = 'loading';
  },
  [CUSTOMER_DOCUMENT_DOWNLOAD_SUCCESS]: (state, payload) => {
    state.customerDocumentDownloadStatus = 'success';
    state.customerDocumentDownloadLink = payload;
  },
  [CUSTOMER_DOCUMENT_DOWNLOAD_ERROR]: state => {
    state.customerDocumentDownloadStatus = 'error';
  },

  [CUSTOMER_UPLOAD_IMAGE]: state => {
    state.customerUploadImageStatus = 'loading';
  },
  [CUSTOMER_UPLOAD_IMAGE_SUCCESS]: state => {
    state.customerUploadImageStatus = 'success';
  },
  [CUSTOMER_UPLOAD_IMAGE_ERROR]: state => {
    state.customerUploadImageStatus = 'error';
  },

  [CUSTOMER_INCREASE_LIMIT]: state => {
    state.customerIncreaseLimitStatus = 'loading';
  },
  [CUSTOMER_INCREASE_LIMIT_SUCCESS]: (state, payload) => {
    if (!isNil(payload)) {
      state.customer = {
        ...state.customer,
        amount: payload.amount,
      };
    }
    state.customerIncreaseLimitStatus = 'success';
  },
  [CUSTOMER_INCREASE_LIMIT_ERROR]: state => {
    state.customerIncreaseLimitStatus = 'error';
  },

  [CUSTOMER_DOWNLOAD_CREDIT_DATABASE]: state => {
    state.customerDownloadCreditDatabaseStatus = 'loading';
  },
  [CUSTOMER_DOWNLOAD_CREDIT_DATABASE_SUCCESS]: (state, payload) => {
    if (!isNil(payload)) {
      state.customer = {
        ...state.customer,
        creditDatabases: payload,
      };
    }
    state.customerDownloadCreditDatabaseStatus = 'success';
  },
  [CUSTOMER_DOWNLOAD_CREDIT_DATABASE_ERROR]: state => {
    state.customerDownloadCreditDatabaseStatus = 'error';
  },

  [CUSTOMER_UPDATE_CONTRACT_DATE]: state => {
    state.customerUpdateContractDateStatus = 'loading';
  },
  [CUSTOMER_UPDATE_CONTRACT_DATE_SUCCESS]: (state, payload) => {
    state.customerUpdateContractDateStatus = 'success';
    const index = findIndex(state.customerList, { id: payload.id });
    state.customerList[index] = payload;
  },
  [CUSTOMER_UPDATE_CONTRACT_DATE_ERROR]: state => {
    state.customerUpdateContractDateStatus = 'error';
  },

  [CUSTOMER_DOWNLOAD_REQUEST]: state => {
    state.customerDownloadStatus = 'loading';
  },
  [CUSTOMER_DOWNLOAD_REQUEST_SUCCESS]: (state, payload) => {
    state.customerDownloadStatus = 'success';
    state.customerDownloadResponse = payload.data;
  },
  [CUSTOMER_DOWNLOAD_REQUEST_ERROR]: state => {
    state.customerDownloadStatus = 'error';
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
